import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import Icon, { IconType } from 'components/Icon';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { MenuItemAction } from 'menu/MenuItem';
import { showContextMenu } from 'redux/mode/actions';
import * as S from './styles';


interface HeaderProps {
	title: string;
	onNewChat: () => void;
	chats: Array<{ id: string; title?: string | null }>;
	activeChatId: string | null;
	onSelectChat: (id: string) => void;
	moreMenuItems: MenuItemAction[];
	showTitle?: boolean;
	showHistory?: boolean;
}

export const Header = ({
	title,
	onNewChat,
	chats,
	activeChatId,
	onSelectChat,
	moreMenuItems,
	showTitle = true,
	showHistory = true,
}: HeaderProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [tooltipId] = useState(() => String(Math.random()));

	const historyMenuItems = useMemo(() => {
		if (chats.length === 0) {
			return [new MenuItemAction('No chats available', '', () => {}, true)];
		}

		// Only show checkmark if activeChatId is actually in the chats list
		// (avoids showing checkmark during transitional state when new chat is being created)
		const activeChatExists =
			activeChatId !== null && chats.some((chat) => chat.id === activeChatId);

		return chats.map((chat) => {
			const isActive = activeChatExists && chat.id === activeChatId;
			return new MenuItemAction(
				chat.title || 'Untitled Chat',
				'',
				() => onSelectChat(chat.id),
				false,
				isActive // showCheckmark
			);
		});
	}, [chats, activeChatId, onSelectChat]);

	const onHeaderContextMenu = (e: React.MouseEvent) => {
		if (!activeChatId) return;
		if (e.target instanceof HTMLElement) {
			dispatch(
				showContextMenu({
					point: { x: e.clientX + 5, y: e.clientY },
					menuItems: [...moreMenuItems],
				})
			);
		}
	};

	const shouldShowMoreMenu = moreMenuItems.length > 0;

	return (
		<S.Base onContextMenu={onHeaderContextMenu}>
			{showTitle && <S.Label>{title}</S.Label>}
			<S.ButtonGroup>
				<S.NewChatButton onClick={onNewChat}>New Chat</S.NewChatButton>
				{showHistory && (
					<DropdownMenu menuItems={historyMenuItems}>
						<S.DropdownTrigger data-tooltip-id={tooltipId} data-tooltip-content="Chat history">
							<Icon type={IconType.HISTORY} width={16} height={16} color={theme.gray[1000]} />
						</S.DropdownTrigger>
					</DropdownMenu>
				)}
				{shouldShowMoreMenu && (
					<DropdownMenu menuItems={moreMenuItems}>
						<S.DropdownTrigger>
							<Icon type={IconType.MORE} width={16} height={16} color={theme.gray[1000]} />
						</S.DropdownTrigger>
					</DropdownMenu>
				)}
				{showHistory && <Tooltip id={tooltipId} placement="top" />}
			</S.ButtonGroup>
		</S.Base>
	);
};
